// Arced text. The panel sends `arcAmount` 0-100: 50 is straight, 0 bends the
// text fully downwards (a smile) and 100 fully upwards (a rainbow).
//
// Fabric 4.5 can lay text along a path, but taken as-is that falls short in two
// ways, both fixed here on the prototype:
//  - the path is fixed. A hard-coded arc is the wrong length for most text, so
//    short text bunches up at the start and long text runs off the end - and a
//    font size or text change leaves it behind again. Here the arc is derived
//    from `arcAmount` on every re-measure, with its length equal to the straight
//    text width: the letters keep the size and spacing they have unarced.
//  - the object is sized to the path, not the letters. A rainbow's box is only
//    as tall as the curve, so the glyphs stick out above the controls and the
//    print-area fit cannot see them. Here the box is fitted to the glyphs.
//
// On the prototype so saved designs, clones and the export renderer all get it:
// `arcAmount` is serialised with the text, and the path is rebuilt from it when
// the design is loaded.

export const ARC_STRAIGHT = 50;

// How far the text bends at 0 or 100: the angle of the circle it covers.
export const MAX_ARC_DEGREES = 180;

// Below this the radius runs away and the curve is not visible anyway.
const MIN_ARC_RADIANS = Math.PI / 360;

// The panel's 0-100 as a signed sweep in radians: positive bends upwards.
export function arcSweep(arcAmount) {
	const amount = Number(arcAmount);
	if (!Number.isFinite(amount)) return 0;
	const bend = (Math.min(100, Math.max(0, amount)) - ARC_STRAIGHT) / ARC_STRAIGHT;
	const sweep = bend * MAX_ARC_DEGREES * Math.PI / 180;
	return Math.abs(sweep) < MIN_ARC_RADIANS ? 0 : sweep;
}

// SVG path for an arc of `length` along its curve, bending by `sweep` radians.
// Runs left to right from the origin, so fabric lays the text out in reading
// order with the letters on the outside of a rainbow and the inside of a smile.
export function arcPathData(length, sweep) {
	const angle = Math.abs(sweep);
	const radius = length / angle;
	const chord = 2 * radius * Math.sin(angle / 2);
	const largeArc = angle > Math.PI ? 1 : 0;
	const clockwise = sweep > 0 ? 1 : 0;
	return `M 0 0 A ${radius} ${radius} 0 ${largeArc} ${clockwise} ${chord} 0`;
}

export default function installTextArc(fabric) {
	const proto = fabric.Text.prototype;
	if (proto.__arcInstalled) return;
	proto.__arcInstalled = true;

	proto._dimensionAffectingProps.push('arcAmount');
	proto.cacheProperties = proto.cacheProperties.concat('arcAmount');

	const initDimensions = proto.initDimensions;
	const toObject = proto.toObject;

	proto.initDimensions = function() {
		if (this.__skipDimension) return;

		// A design saved before arcAmount existed comes back with its path as the
		// plain object toObject wrote, which fabric cannot measure against.
		if (this.path && !(this.path instanceof fabric.Path)) {
			this.path = new fabric.Path(this.path.path, this.path);
			this.setPathInfo();
		}

		const sweep = arcSweep(this.arcAmount);
		if (this.arcAmount != null) {
			// Measure straight first: that width is the length the arc gets.
			this.path = null;
			initDimensions.call(this);
			if (sweep === 0) return;
			this.path = new fabric.Path(arcPathData(this.width, sweep));
			this.setPathInfo();
			this.dirty = true;
		}

		initDimensions.call(this);
		if (this.path) fitBoxToGlyphs(this);
	};

	proto.toObject = function(propertiesToInclude) {
		const obj = toObject.call(this, propertiesToInclude);
		if (this.arcAmount != null) obj.arcAmount = this.arcAmount;
		return obj;
	};
}

// Replace the path-sized box with one around the letters, and move the letters
// so they sit centred in it - fabric draws them relative to the box centre.
function fitBoxToGlyphs(text) {
	// Lays the glyphs out along the path; the positions are cached per line and
	// reused when drawing, so the shift below sticks.
	text.calcTextWidth();

	const ascent = text._fontSizeMult - text._fontSizeFraction;
	const descent = text._fontSizeFraction;
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	const glyphs = [];

	(text.__charBounds || []).forEach(line => (line || []).forEach(box => {
		if (!box || box.renderLeft === undefined) return;
		glyphs.push(box);
		const cos = Math.cos(box.angle), sin = Math.sin(box.angle);
		const halfWidth = box.kernedWidth / 2;
		[[-halfWidth, -box.height * ascent], [halfWidth, -box.height * ascent],
			[-halfWidth, box.height * descent], [halfWidth, box.height * descent]].forEach(([x, y]) => {
			const px = box.renderLeft + x * cos - y * sin;
			const py = box.renderTop + x * sin + y * cos;
			minX = Math.min(minX, px); maxX = Math.max(maxX, px);
			minY = Math.min(minY, py); maxY = Math.max(maxY, py);
		});
	}));
	if (!glyphs.length) return;

	const centerX = (minX + maxX) / 2, centerY = (minY + maxY) / 2;
	glyphs.forEach(box => {
		box.renderLeft -= centerX;
		box.renderTop -= centerY;
	});
	text.width = maxX - minX;
	text.height = maxY - minY;
}
